import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Truck, CheckCircle2, AlertTriangle, MapPin, Clock, Phone, Ruler, ShieldCheck, ArrowRight, Droplets, Sprout, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
export function DeliveryPrep() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  const stagger = {
    initial: {
      opacity: 0
    },
    whileInView: {
      opacity: 1
    },
    viewport: {
      once: true
    },
    transition: {
      staggerChildren: 0.1
    }
  };
  const item = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5
    }
  };
  return <div className="pt-24 pb-16 bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-1426">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#333030] dark:bg-[#333030] overflow-hidden border-b border-stone-200 dark:border-white/10" data-id="element-1427">
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#2b2828]/50 to-transparent pointer-events-none" data-id="element-1428" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1429">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          damping: 20,
          stiffness: 200
        }} className="w-20 h-20 bg-brand-orange/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-brand-orange/20" data-id="element-1430">
            <Truck className="w-10 h-10 text-brand-orange" data-id="element-1431" />
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.5
        }} className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6" data-id="element-1432">
            Prepare for Your <span className="text-brand-orange" data-id="element-1433">Delivery</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto font-medium" data-id="element-1434">
            Follow this simple guide to ensure a smooth, safe, and successful
            material delivery to your home or job site.
          </motion.p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16" data-id="element-1435">
        {/* Before Delivery Day */}
        <motion.section {...fadeIn} data-id="element-1436">
          <div className="flex items-center gap-4 mb-8" data-id="element-1437">
            <div className="w-12 h-12 bg-[#2e8a44]/10 rounded-xl flex items-center justify-center border border-[#2e8a44]/20" data-id="element-1438">
              <CalendarDays className="w-6 h-6 text-[#2e8a44]" data-id="element-1439" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1440">
              Before Delivery Day
            </h2>
          </div>
          <motion.div variants={stagger} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1441">
            {[{
            icon: <Truck className="w-5 h-5" data-id="element-1442" />,
            text: 'Clear the dump area of vehicles, toys, and obstacles'
          }, {
            icon: <MapPin className="w-5 h-5" data-id="element-1443" />,
            text: 'Mark the exact spot where you want material dumped (use a cone, tarp, or flag)'
          }, {
            icon: <ShieldCheck className="w-5 h-5" data-id="element-1444" />,
            text: 'Ensure gate access if fenced and provide gate codes if applicable'
          }, {
            icon: <Ruler className="w-5 h-5" data-id="element-1445" />,
            text: 'Measure clearance: Trucks are 10ft wide, 12ft tall, and 30ft long'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-1446" />,
            text: 'Check for low-hanging overhead wires or tree branches'
          }, {
            icon: <CheckCircle2 className="w-5 h-5" data-id="element-1447" />,
            text: 'Confirm someone 18+ will be on-site to receive the delivery'
          }].map((point, i) => <motion.div key={i} variants={item} className="flex items-start gap-3 p-4 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-1448">
                <div className="text-brand-orange shrink-0 mt-0.5" data-id="element-1449">
                  {point.icon}
                </div>
                <p className="text-brand-charcoal dark:text-stone-200 font-medium" data-id="element-1450">
                  {point.text}
                </p>
              </motion.div>)}
          </motion.div>
        </motion.section>

        {/* Day of Delivery */}
        <motion.section {...fadeIn} data-id="element-1451">
          <div className="flex items-center gap-4 mb-8" data-id="element-1452">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20" data-id="element-1453">
              <Clock className="w-6 h-6 text-brand-orange" data-id="element-1454" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1455">
              Day of Delivery
            </h2>
          </div>
          <div className="bg-white dark:bg-[#2a2a2a] rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm overflow-hidden" data-id="element-1456">
            <div className="divide-y divide-stone-200 dark:divide-white/10" data-id="element-1457">
              {[{
              icon: <Phone className="w-6 h-6" data-id="element-1458" />,
              title: 'Driver will call',
              desc: 'Expect a call 15-30 minutes before the driver arrives at your location.'
            }, {
              icon: <MapPin className="w-6 h-6" data-id="element-1459" />,
              title: 'Point the way',
              desc: 'Direct the driver to your marked dump spot upon arrival.'
            }, {
              icon: <AlertTriangle className="w-6 h-6" data-id="element-1460" />,
              title: 'Stay clear',
              desc: 'Keep children, pets, and yourself at a safe distance during the dumping process.'
            }, {
              icon: <CheckCircle2 className="w-6 h-6" data-id="element-1461" />,
              title: 'Inspect material',
              desc: 'Verify the material is correct before the driver leaves the site.'
            }].map((step, i) => <div key={i} className="p-6 flex items-start gap-4" data-id="element-1462">
                  <div className="w-10 h-10 rounded-full bg-stone-100 dark:bg-white/5 flex items-center justify-center text-brand-muted shrink-0" data-id="element-1463">
                    {step.icon}
                  </div>
                  <div data-id="element-1464">
                    <h3 className="text-lg font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-1465">
                      {step.title}
                    </h3>
                    <p className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-1466">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.section>

        {/* Access Tips */}
        <motion.section {...fadeIn} data-id="element-1467">
          <div className="flex items-center gap-4 mb-8" data-id="element-1468">
            <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center border border-blue-500/20" data-id="element-1469">
              <Truck className="w-6 h-6 text-blue-500" data-id="element-1470" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1471">
              Access Tips
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1472">
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-1473">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1474">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-1475" />
                Firm Ground Required
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1476">
                Fully loaded trucks are extremely heavy. They need firm ground
                or pavement to drive on, not soggy lawns or soft dirt, to avoid
                getting stuck or causing damage.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-1477">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1478">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-1479" />
                Steep Driveways
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1480">
                Very steep driveways may be unsafe for a dump truck to navigate
                or dump on. In these cases, a street-level dump may be required.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm" data-id="element-1481">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1482">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-1483" />
                Cul-de-sacs & Tight Turns
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1484">
                Large dump trucks need significant turning radius. Ensure there
                is adequate room to maneuver in cul-de-sacs or tight
                neighborhoods.
              </p>
            </div>
            <div className="p-6 bg-brand-orange/10 dark:bg-brand-orange/10 rounded-xl border border-brand-orange/30 shadow-sm flex flex-col justify-center" data-id="element-1485">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1486">
                <Phone className="w-5 h-5 text-brand-orange" data-id="element-1487" />
                Unsure about access?
              </h3>
              <p className="text-brand-muted dark:text-stone-300 text-sm font-medium mb-3" data-id="element-1488">
                If you have concerns about your delivery location, call us first
                and we can review a photo of your site.
              </p>
              <a href="tel:206-619-3742" className="inline-flex items-center text-brand-orange font-bold hover:underline" data-id="element-1489">
                Call (206) 619-3742 <ArrowRight className="w-4 h-4 ml-1" data-id="element-1490" />
              </a>
            </div>
          </div>
        </motion.section>

        {/* After Delivery */}
        <motion.section {...fadeIn} data-id="element-1491">
          <div className="flex items-center gap-4 mb-8" data-id="element-1492">
            <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center border border-purple-500/20" data-id="element-1493">
              <Sprout className="w-6 h-6 text-purple-500" data-id="element-1494" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1495">
              After Delivery
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1496">
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-1497">
              <Clock className="w-8 h-8 text-brand-muted mx-auto mb-3" data-id="element-1498" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1499">
                Spread Quickly
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1500">
                Spread material within 48 hours to avoid settling and compaction
                in the pile.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-1501">
              <Droplets className="w-8 h-8 text-brand-muted mx-auto mb-3" data-id="element-1502" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1503">
                Wet Before Compacting
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1504">
                Lightly wet gravel before using a plate compactor for maximum
                density and stability.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#2a2a2a] rounded-xl border border-stone-200 dark:border-white/10 shadow-sm text-center" data-id="element-1505">
              <Sprout className="w-8 h-8 text-brand-muted mx-auto mb-3" data-id="element-1506" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1507">
                Protect Trees
              </h3>
              <p className="text-brand-muted dark:text-stone-400 text-sm font-medium" data-id="element-1508">
                When spreading bark or mulch, keep it at least 3 inches away
                from tree trunks to prevent rot.
              </p>
            </div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section {...fadeIn} className="bg-brand-charcoal dark:bg-[#1a1a1a] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-1509">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none" data-id="element-1510"></div>
          <div className="relative z-10" data-id="element-1511">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-4" data-id="element-1512">
              Ready to order?
            </h2>
            <p className="text-stone-300 text-lg mb-8 max-w-xl mx-auto font-medium" data-id="element-1513">
              Now that you know how to prepare, head over to our Materials Hub
              to select your products and schedule your delivery.
            </p>
            <Link to="/#materials" className="inline-flex items-center justify-center bg-brand-orange hover:bg-brand-orangeHover text-white px-8 py-4 rounded-xl font-extrabold text-lg transition-all shadow-[0_0_20px_rgba(255,140,50,0.3)] hover:shadow-[0_0_25px_rgba(255,140,50,0.5)]" data-id="element-1514">
              Browse Materials Hub
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-1515" />
            </Link>
          </div>
        </motion.section>
      </div>
    </div>;
}